// 認証セッションのライフサイクルイベント。
//
// `apiRequest` (React の外側) と `AuthContext` / `AdminAuthContext`
// (React の中) の間で「セッションが切れた」ことを伝えるために使う。
// React Context から関数を直接呼ぶと循環参照になりがちなので、
// シンプルな pub-sub にしている。
//
// Scope:
// - 'performer': /v1/... へのリクエストで refresh も失敗した状態
// - 'admin'    : /v1/admin/... へのリクエストで refresh も失敗した状態
// 両方独立。performer のセッションが切れても admin のトークンは消さない、
// 逆も同様。

export type AuthScope = 'performer' | 'admin';

type Listener = () => void;

const listeners: Record<AuthScope, Set<Listener>> = {
  performer: new Set(),
  admin: new Set(),
};

export function onAuthExpired(scope: AuthScope, fn: Listener): () => void {
  listeners[scope].add(fn);
  return () => {
    listeners[scope].delete(fn);
  };
}

export function emitAuthExpired(scope: AuthScope): void {
  // listener 実行中に subscribe が解除される可能性があるので copy
  for (const fn of [...listeners[scope]]) {
    try {
      fn();
    } catch {
      // listener の例外は他の listener を巻き込まない
    }
  }
}

// ----------------------------------------------------------------
// セッション期限切れトースト用の sessionStorage フラッシュ
// ----------------------------------------------------------------
//
// `expireSession` が呼ばれた時点で「期限切れになった scope」を sessionStorage に
// 1 件だけ保存し、リダイレクト先のログイン画面が mount 時に拾ってトーストを表示
// する。React Context (Toast Provider) と AuthContext の循環依存を避ける目的。
//
// sessionStorage を使う理由:
// - 同じタブ内でしか共有しない(別タブの期限切れに巻き込まれない)
// - リロードを跨いでも消えるので「何度もトーストが出る」事故を防ぐ
//   (consume 時に必ず削除する)

const EXPIRED_FLASH_KEY = 'safelens.auth.expired_flash';

interface ExpiredFlashRecord {
  scope: AuthScope;
  // expireSession が呼ばれた時刻(ms)。極端に古いフラッシュは無視するため。
  at: number;
}

// フラッシュの有効期限。これより古いものは無視する(タブを長く放置していた
// 場合の誤発火対策)。
const EXPIRED_FLASH_TTL_MS = 60_000;

export function setExpiredFlash(scope: AuthScope): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const record: ExpiredFlashRecord = { scope, at: Date.now() };
    sessionStorage.setItem(EXPIRED_FLASH_KEY, JSON.stringify(record));
  } catch {
    // QuotaExceededError 等は無視(トーストは best-effort)
  }
}

// 1 度だけフラッシュを取り出して削除する。読み出し成功時のみ scope を返す。
// TTL を過ぎていたら null を返してクリアする。
//
// `expectScope` を指定すると、その scope のフラッシュ「だけ」を消費する。
// 例: performer 用の /login で `consumeExpiredFlash('performer')` を呼ぶと、
// admin 側のフラッシュがあっても触らずに残し、admin /login が消化できる。
export function consumeExpiredFlash(expectScope?: AuthScope): AuthScope | null {
  if (typeof sessionStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(EXPIRED_FLASH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: Partial<ExpiredFlashRecord> | null = null;
  try {
    parsed = JSON.parse(raw) as Partial<ExpiredFlashRecord> | null;
  } catch {
    // 壊れた JSON はクリアして null
    try {
      sessionStorage.removeItem(EXPIRED_FLASH_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
  if (!parsed || (parsed.scope !== 'admin' && parsed.scope !== 'performer')) {
    try {
      sessionStorage.removeItem(EXPIRED_FLASH_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
  if (typeof parsed.at !== 'number') {
    try {
      sessionStorage.removeItem(EXPIRED_FLASH_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
  // scope が一致しないなら触らずにそのまま残す
  if (expectScope && parsed.scope !== expectScope) {
    return null;
  }
  // ここまで来たら消費して返す
  try {
    sessionStorage.removeItem(EXPIRED_FLASH_KEY);
  } catch {
    /* ignore */
  }
  if (Date.now() - parsed.at > EXPIRED_FLASH_TTL_MS) return null;
  return parsed.scope;
}
